import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Panel } from '@anwb/poncho/components/panel';
import Tooltip from '@anwb/poncho/components/tooltip';
import Typography from '@anwb/poncho/components/typography';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { setIdForPricePoint, setKilometrageChoice, setPriceForPricePoint } from 'store/actions';
import { setCookie } from 'utils';
import { trackEvent } from 'utils/trackEvent';

import { KilometrageFull } from './KilometrageFull';
import { useDefaultKilometrage } from './utils';
import { customTooltipTaggingEvent } from './utils/datalayerEvent';

type KilometrageProps = {
	confirmOnChange?: boolean;
	value?: string;
	onChange?: (value: string) => void;
};

export const Kilometrage = ({ confirmOnChange = false, value, onChange }: KilometrageProps) => {
	const {
		selectedKilometrage,
		availableKilometrages,
		cheapestKilometrage,
		groupedPricePoints,
		selectedDuration,
	} = useDefaultKilometrage();

	const dispatch = useDispatch();
	const gaFunnelInfo = useGaFunnelInfo();

	const handleChange = useCallback(
		(newValue: string | string[]) => {
			if (typeof newValue === 'string' && selectedKilometrage !== newValue) {
				if (confirmOnChange && onChange) {
					onChange(newValue);
					return;
				}

				setCookie('kilometrage', newValue);

				// Add null check for groupedPricePoints
				if (groupedPricePoints && selectedDuration) {
					const kilometrageObject = groupedPricePoints[Number(selectedDuration)]?.kilometrages.find(
						(kilometrage) => kilometrage.mileage.toString() === newValue
					);

					if (kilometrageObject) {
						dispatch(setKilometrageChoice(newValue));
						dispatch(setIdForPricePoint(kilometrageObject.id));
						dispatch(setPriceForPricePoint(kilometrageObject.pricePerMonth));
						trackEvent({
							event: 'user_interaction',
							type: 'form',
							component: 'form-input-radiobutton',
							action: 'change',
							category: 'kilometrage',
							variant: kilometrageObject.description,
							...gaFunnelInfo,
						});
					}
				}
			}
		},
		[
			selectedKilometrage,
			confirmOnChange,
			onChange,
			dispatch,
			gaFunnelInfo,
			groupedPricePoints,
			selectedDuration,
		]
	);

	const selected = value ?? selectedKilometrage ?? cheapestKilometrage?.mileage.toString() ?? '';

	return (
		<Panel data-test="kilometrage" hasOuterSpacing={false}>
			<Panel.Content>
				<Typography variant="component-title">
					Hoeveel kilometers per jaar wil je rijden?
					<Tooltip
						dataTest="tooltip-hoeveel-kilometers"
						customTaggingEvent={(_?: string, isOpen?: boolean) => {
							customTooltipTaggingEvent(
								'Hoeveel kilometers per jaar wil je rijden',
								isOpen,
								gaFunnelInfo
							);
						}}
					>
						Er worden geen extra kosten berekend zolang je binnen de door jou gekozen
						kilometerbundel blijft. Maak je meer kilometers en val je daarmee in een andere bundel
						dan betaal je het hogere tarief van die bundel, rijd je minder dan afgesproken krijg je
						het teveel betaalde bedrag terug. Dit wordt met terugwerkende kracht verrekend.
					</Tooltip>
				</Typography>
				<KilometrageFull
					availableKilometrages={availableKilometrages}
					handleChange={handleChange}
					selectedValue={selected}
					customKey={selectedDuration}
				/>
			</Panel.Content>
		</Panel>
	);
};
